import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import {
  Card,
  Container,
  LinearProgress,
  Typography,
  Avatar,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  Stack,
  TableRow,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
// import LoadingButton from '@mui/lab';
import { styled } from '@mui/material/styles';
import { fDateTime } from '../utils/formatTime';
import Scrollbar from '../components/scrollbar/Scrollbar';
import { useFetchItem } from '../services/item_fetch_service';
import { formatImage } from '../utils/formatImage';
import { useFetchCoachSessions } from '../services/sessions_service';
// import { useFetchCoachProgram } from '../services/programs_service';
import { useFetchCoachPrograms } from '../services/programs_service';
import { UserListHead } from '../sections/@dashboard/user';
import i18n from '../i18n';
import QualificationSection from '../components/qualification-section';

// ----------------------------------------------------------------------


export default function CoachDetailsPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data: coach, isLoading } = useFetchItem('coaches', id);

  return (
    <>
      <Helmet>
        <title>
          {t('Coach')} | {coach?.name ?? ''}
        </title>
      </Helmet>
      {isLoading && <LinearProgress />}
      {!isLoading && (
        <Container>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: 16,
              height: 480,
              overflow: 'hidden',
              backgroundColor: 'rgba(0, 0, 0, 0)',
              backgroundAttachment: 'scroll, scroll',
              backgroundImage: `linear-gradient(rgba(22, 28, 36, 0.64) 0%, rgba(22, 28, 36, 0.64) 100%), url(${formatImage(
                coach?.page_image ?? coach?.thumbnail ?? coach?.wide_image ?? coach?.image,
                { h: 480 }
              )})`,
              backgroundOrigin: 'padding-box, padding-box',
              backgroundClip: 'border-box, border-box',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
            }}
          >
            <Typography color={'white'} variant="h4" sx={{ mb: 5, flex: 1 }}>
              {t('Coach')} | {coach?.name}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={5}
            >
              {/* Display relevant sections for coach details */}

              <CoachSection id={id} />
            </Stack>
          </div>
        </Container>
      )}

      {!isLoading && (
        <Container>
          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {t('Qualifications')}
          </Typography>

          <QualificationSection id={coach.id}/>

          <div style={{ width: '100%', height: '1px', backgroundColor: 'lightgray', marginTop: '2.5rem' }} />

          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {t('Programs')}
          </Typography>
          {<ProgramSection programId={id} />}
          <div style={{ width: '100%', height: '1px', backgroundColor: 'lightgray', marginTop: '2.5rem' }} />

          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {t('Sessions')}
          </Typography>

          {<SessionSection programId={id} />}
        </Container>
      )}
    </>
  );
}

function CoachSection({ id }: { id: string }) {
  const { data: coach, isLoading } = useFetchItem('coaches', id);

  if (isLoading || !coach) {
    return <div />;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Avatar
        alt={coach.name}
        src={formatImage(coach.image, { w: 128, h: 128 })}
        sx={{ width: 64, height: 64, marginRight: '16px' }}
      />

      <div>
        <Typography color={'white'} variant="body1" sx={{ marginBottom: '4px' }}>
          {coach.profession}
        </Typography>
        <Typography color={'white'} variant="body2">
          {coach.city}, {coach.country}
        </Typography>
        <Typography color={'white'} variant="body2">
          {coach.email} | {coach.phone}
        </Typography>
      </div>
    </div>
  );
}

function SessionSection({ programId }: { programId: string }) {
  const { sessions, isLoading } = useFetchCoachSessions(programId);
  const { t } = useTranslation();

  const [editingSession, setEditingSession] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');



  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading || !sessions) return <div />;

  if (sessions && sessions.length === 0)
    return (
      <Typography variant="body2" sx={{ mb: 5 }}>
        {t('No Sessions')}
      </Typography>
    );

  return (
    <Card>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
            <TableBody>
              {sessions.map((row) => {
                const { id, name, image, sessionType, scheduledFor } = row;

                return (
                  <TableRow hover key={id} tabIndex={-1} role="checkbox">
                    <TableCell component="th" scope="row">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar alt={name} src={image} />
                        <Typography variant="subtitle2" noWrap>
                          {name}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="left">{t(sessionType)}</TableCell>

                    <TableCell align="left">{fDateTime(scheduledFor.toMillis())}</TableCell>
                  </TableRow>
                );
              })}
              {sessions.length === 0 && (
                <TableRow style={{ height: 53 }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Card>
  );
}

function ProgramSection({ programId }: { programId: string }) {
  const { programs, isLoading } = useFetchCoachPrograms(programId);
  const { t } = useTranslation();

  const [editingProgram, setEditingProgram] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');



  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (isLoading || !programs) return <div />;

  if (programs && programs.length === 0)
    return (
      <Typography variant="body2" sx={{ mb: 5 }}>
        {t('No Programs')}
      </Typography>
    );

  return (
    <Card>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD3} onRequestSort={handleRequestSort} />
            <TableBody>
              {programs.map((row) => {
                const { id, name, thumbnail, description, packages, numberOfParticipants, currency, rating, tags } =
                  row;

                return (
                  <TableRow hover key={id} tabIndex={-1} role="checkbox">
                    <TableCell component="th" scope="row">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar alt={name} src={thumbnail} />
                        <Typography variant="subtitle2" noWrap>
                          {name}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="left">{t(description)}</TableCell>
                    <TableCell align="left">
                      <ul>
                        {packages && packages.length !== 0
                          ? packages.map((p) => (
                              <li key={p.id}>
                                <b>{p.name}</b>: {p.total} {currency}
                              </li>
                            ))
                          : t('Empty')}
                      </ul>
                    </TableCell>
                    <TableCell align="left">{t(numberOfParticipants)}</TableCell>
                    <TableCell align="left">{t(rating)}</TableCell>
                    <TableCell align="left">{(tags ?? []).join(', ')}</TableCell>
                  </TableRow>
                );
              })}
              {programs.length === 0 && (
                <TableRow style={{ height: 53 }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Card>
  );
}

const TABLE_HEAD = [
  { id: 'name', label: i18n.t('Name'), alignRight: false },
  { id: 'sessionType', label: i18n.t('Module'), alignRight: false },
  { id: 'scheduledFor', label: i18n.t('Scheduled For'), alignRight: false },
  {},
];


const TABLE_HEAD3 = [
  { id: 'name', label: i18n.t('Name'), alignRight: false },
  { id: 'description', label: i18n.t('Description'), alignRight: false },
  { id: 'packaages', label: i18n.t('Packages'), alignRight: false },
  { id: 'numberOfParticipants', label: i18n.t('Participants'), alignRight: false },
  { id: 'rating', label: i18n.t('Rating'), alignRight: false },
  { id: 'tags', label: i18n.t('Tags'), alignRight: false },
  {},
];
